import type { ReactNode } from "react";

import type { ManualStage, Stage } from "@/lib/stages";
import styles from "./Stages.module.css";

type PointKey = "new" | ManualStage | "planning" | "calculation";

/**
 * Six points (design 2026-09-13 §9; UX gate round 1, #5): „Neu" first, as
 * `lib/stages` names a fresh Vorgang, then the two the system sets and the
 * three a person may.
 */
const POINTS: { key: PointKey; word: string }[] = [
  { key: "new", word: "Neu" },
  { key: "documents", word: "Unterlagen" },
  { key: "planning", word: "Planung" },
  { key: "calculation", word: "Kalkulation" },
  { key: "sent", word: "Angebot raus" },
  { key: "outcome", word: "Gewonnen" },
];

/**
 * Where the Vorgang stands now — the one point that is neither behind nor
 * ahead. Read from the same `Stage` every list shows, so the path and the
 * chip never disagree. A finished Vorgang (abgewiesen, storniert,
 * archiviert) and a status this bundle does not know stand nowhere on it.
 */
function currentPoint(stage: Stage): PointKey | null {
  if (stage.finished) return null;
  if (stage.done.includes("outcome")) return "outcome";
  if (stage.done.includes("sent")) return "sent";
  if (stage.percent >= 80) return "calculation";
  if (stage.percent >= 50) return "planning";
  if (stage.percent >= 20) return "documents";
  // `stageOf`'s fallback for an unknown status is resting at 0 — not „Neu".
  return stage.resting ? null : "new";
}

/**
 * The next step's verb, as the button in its point's place says it (and a
 * register row's one gesture). Here because the path keeps each point's
 * place as wide as the widest thing it may hold (round-5 screen check,
 * R5-9); `StageControl` renders the button with the same words.
 *
 * UX gate round 1, #7 / round 2, #8: a verb, not the chip's state —
 * „Unterlagen abhaken" without a check mark; „Angebot raus" keeps its mark.
 * Fix round 3, C1: „Gewonnen / Verloren …" (144 px) was the widest label by
 * half and clipped in every step column it was given; the short word keeps
 * the column narrow, the whole question is its accessible name.
 */
export const ACTION_WORD: Record<ManualStage, string> = {
  documents: "Unterlagen abhaken",
  sent: "Angebot raus",
  outcome: "Ausgang …",
};

/**
 * The widest word a point may show — bold, as the current point sets it:
 * „Planung · ruht" where a resting Vorgang stands, „Gewonnen" over
 * „Verloren".
 */
function widestWord(key: PointKey, word: string): string {
  return key === "planning" ? `${word} · ruht` : word;
}

/** Behind the Vorgang when there is no current point: what its percent and ticks say. */
function doneWithoutCurrent(key: PointKey, stage: Stage): boolean {
  switch (key) {
    case "new":
      return stage.finished;
    case "planning":
      return stage.percent >= 50;
    case "calculation":
      return stage.percent >= 80;
    default:
      return stage.done.includes(key);
  }
}

/**
 * The path a Vorgang runs, as words (UX gate round 1, #5). Its points
 * offer nothing to press — the next step a person can take is a button with
 * its verb, standing in its point's place (`action`, UX gate round 4, #10),
 * not a point that looks like a radio button. Three looks, each also without colour: behind — a filled dot
 * in the control accent (graphite in the light) and a spoken „erledigt"; now — a filled ink dot, the word in bold ink
 * and underlined, and `aria-current="step"`; ahead — an empty ring in ink-3.
 * No ring with a halo for „now" any more (UX gate round 3, #6): a ring
 * around a centre is a radio button, and the path offers no choice.
 * Graphite and ink, never brand red: the path lives in the record's head,
 * the data area (CI §2.1). No percent anywhere (CI §6, Präzisierung 13.09.):
 * the word is the stage.
 */
export function StagePath({
  stage,
  action = null,
}: {
  stage: Stage;
  /**
   * The next step a person can take, as a button in place of its point's
   * word (UX gate round 4, #10): „Unterlagen abhaken", „Angebot raus",
   * „Ausgang …" stand where the path goes next. As a row of its own under
   * the path it made the record's head 36 px taller in the stages that
   * have one, and the head's buttons jumped by as much.
   */
  action?: ReactNode;
}) {
  const current = currentPoint(stage);
  const currentIndex = POINTS.findIndex((point) => point.key === current);
  const acting = action !== null && !stage.resting ? stage.next : null;
  return (
    // The frame is what the compact variant measures (register fix round 3).
    <div className={styles.pathFrame}>
      <ol className={styles.path} aria-label="Stadien">
        {POINTS.map(({ key, word }, index) => {
          const isCurrent = index === currentIndex;
          const done = currentIndex >= 0 ? index < currentIndex : doneWithoutCurrent(key, stage);
          // A lost deal ends on its own word, not on a filled „Gewonnen".
          const shown = key === "outcome" && stage.word === "Verloren" ? "Verloren" : word;
          const cls = `${styles.point} ${done ? styles.done : ""} ${isCurrent ? styles.current : ""}`;
          const verb = key === "new" || key === "planning" || key === "calculation" ? undefined : ACTION_WORD[key];
          return (
            <li key={key} className={cls} aria-current={isCurrent ? "step" : undefined}>
              <span className={styles.static}>
                <span className={styles.dot} aria-hidden="true" />
                {/* The point's place, as wide in every stage as the widest
                    thing it may hold (round-5 screen check, R5-9): its word
                    in bold, and the button with its verb where the next step
                    may stand — held by invisible copies of no height
                    (`data-ghost`, `data-action`), so no word of the path
                    moves sideways when the Vorgang moves on. */}
                <span
                  className={styles.slot}
                  data-ghost={widestWord(key, word)}
                  data-action={verb}
                  data-action-icon={key === "sent" ? "" : undefined}
                >
                  <span className={styles.slotRow}>
                    {key === acting ? action : <span className={styles.word}>{shown}</span>}
                    {isCurrent && stage.resting ? <span className={styles.resting}> · ruht</span> : null}
                    {/* A filled dot is the whole difference between a stage that
                        is behind us and one still ahead — and a filled dot is
                        exactly what is not read out. */}
                    {done ? <span className="visually-hidden"> erledigt</span> : null}
                  </span>
                </span>
              </span>
            </li>
          );
        })}
      </ol>
      {/* A finished Vorgang stands nowhere on the path (Task 12, from Task
          9): on a phone, where only the current point and the next are
          shown, the path showed nothing. There the finished state stands as
          one pill in the chip's word; wide it is not shown — the path says
          it as it always did. */}
      {currentIndex < 0 ? (
        <p className={styles.ended}>
          <span className="visually-hidden">Stadium: </span>
          {stage.word}
        </p>
      ) : null}
    </div>
  );
}
